import React from 'react';
import { clockTime, deviceZone, formatIn } from '../../../../../function-utils/building-time';
import { stayZoneNote } from '../../../../onboarding/stay/stay-steps';
import { Muted, TimeText } from './guide.styled';

/**
 * THE GUIDE'S TIMES AND LINKS (plan S7.7).
 *
 * Every guest time is on the BUILDING's clock: the body's `zone`, through
 * building-time.ts (the one module allowed to format a date), with the S6
 * clock note beside it ("Times are Lisbon time.", data-clock-whose="building")
 * and the instant on the element (`data-at`).
 */

/** "15:00" in the building's zone. */
export const timeIn = (iso: string | null | undefined, zone: string): string => clockTime(iso, zone);

/** "Friday 24 October" in the building's zone. */
export const longDateIn = (iso: string | null | undefined, zone: string): string =>
  formatIn(iso, zone, { weekday: 'long', day: 'numeric', month: 'long' });

/** "Fri 24 Oct" in the building's zone. */
export const shortDateIn = (iso: string | null | undefined, zone: string): string =>
  formatIn(iso, zone, { weekday: 'short', day: 'numeric', month: 'short' });

/** One rendered time: a <time> carrying its instant. */
export const At: React.FC<{ iso: string | null | undefined; children: React.ReactNode }> = ({ iso, children }) => (
  <TimeText dateTime={iso || undefined} data-at={iso || undefined}>
    {children}
  </TimeText>
);

/** The building clock note (S6's words and attributes). */
export const BuildingClockNote: React.FC<{ zone: string }> = ({ zone }) => (
  <Muted data-clock-note="" data-clock-whose="building">
    {stayZoneNote(zone)}
  </Muted>
);

/**
 * The device-clock note, for a time the server gave without a zone (the
 * PIN-lock answer carries `pin.zone` since S7.7; this is the fallback).
 */
export const DeviceClockNote: React.FC = () => (
  <Muted data-clock-note="" data-clock-whose="viewer">
    {stayZoneNote(deviceZone())}
  </Muted>
);

export const deviceTimeOf = (iso: string | null | undefined): string => clockTime(iso, deviceZone());

/** The host's maps link, else a maps search for `query`; '' when there is neither. */
export const mapsHref = (mapsUrl: string, query: string): string => {
  const own = (mapsUrl || '').trim();
  if (/^https?:\/\//i.test(own)) return own;
  const q = (query || '').trim();

  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : '';
};

/** A tel: link for a phone as the host wrote it ('' when it has no digits). */
export const telHref = (phone: string | null | undefined): string => {
  const digits = String(phone || '').replace(/[^\d+]/g, '');

  return /\d/.test(digits) ? `tel:${digits}` : '';
};

/** Host-written text is in the guide's language when the host set one. */
export const langOf = (contentLanguage: string | null | undefined): string | undefined =>
  contentLanguage ? contentLanguage : undefined;
